import { Component, DoCheck, effect, EventEmitter, forwardRef, inject, input, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ISelectOption, SelectComponent } from "@tylertech/forge";
import { ForgeDividerModule, ForgeOptionModule, ForgeSelectProxyModule } from "@tylertech/forge-angular";
import { Utilities, } from "@tylertech/shared-module";
import { IFormMetadata, IViewMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { AugmentedModelObject, RepositoryFormData } from "../../../interfaces";
import { MetadataErrorService, MetadataService, MetadataViewService, MultipleEntityEditService, ViewShortcutService } from "../../../services";
import { ArrayHelpers } from "../../../utilities";
import { MetadataViewComponent } from "../metadata.view.component";
import { MetadataNavigationTreeComponent } from "./metadataNavigationTree.component";
import { Leaf, OnContextEvent, Tree, TreeNode } from "./tree";

const VIEW_PATH_SEPARATOR = "/";

@Component({
    selector: "metadata-view-navigation-list",
    templateUrl: "./metadataViewNavigationList.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ForgeDividerModule,
        ForgeOptionModule,
        ForgeSelectProxyModule,
        FormsModule,
        MetadataNavigationTreeComponent,
        forwardRef(() => MetadataViewComponent),
        ReactiveFormsModule,
    ]
})
export class MetadataViewNavigationListComponent implements OnDestroy, DoCheck
{
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly $multipleEditEntityService = inject(MultipleEntityEditService);
    private $viewShortcutService = inject(ViewShortcutService);

    @Input()
    readonly formMetadata: Readonly<IFormMetadata>;

    @Input()
    readonly model: Readonly<AugmentedModelObject>;

    @Input()
    readonly datacontroller: RepositoryFormData;

    @Output()
    readonly onSelect = new EventEmitter<IViewMetadata>();

    @Input()
    viewOnly: boolean = false;

    @Input()
    get selectedView(): IViewMetadata
    {
        return this._selectedView;
    }

    set selectedView(value: IViewMetadata)
    {
        this._selectedView = value;
        const doScroll = this.isInitialized && !this.selecting;
        this.updateNavTree(this.formMetadata.Views.filter(view => !this.isHidden(view)));
        if (doScroll) this.scrollIntoView();
        this.selecting = false;
    }

    readonly formId = input<string>();

    @ViewChild("shortcut") shortcut: SelectComponent;

    get hasShortcuts(): boolean
    {
        return this.formMetadata.EnableViewShortcuts && this.shortcutViews.length > 0;
    }

    get shortcutViews(): ISelectOption[]
    {
        return this.$viewShortcutService.ShortcutViews.filter(x => this.hiddenShortcuts.indexOf(x.value) < 0);
    }

    hiddenShortcuts: string[] = [];

    private _selectedView: IViewMetadata;

    private _navTree: Tree;

    private isInitialized: boolean;

    selecting: boolean = false;

    constructor()
    {
        effect(() =>
        {
            this.isInitialized = false;
            const id = this.formId();
            if (!id)
                return;
            requestAnimationFrame(() => this.setupNavTree(this.formMetadata));
        });
    }

    private treeRebuild$ = new Subject<void>();

    ngOnDestroy(): void
    {
        this.treeRebuild$.next();
        this.treeRebuild$.complete();
        this.$viewShortcutService.ShortcutViews = [];
    }

    setupNavTree(formMetadata: IFormMetadata): void
    {
        if (this.$multipleEditEntityService.MultipleEditLoading)
            return;
        this.updateNavTree(formMetadata.Views.filter(view => !this.isHidden(view)));
        if (this.menuView)
            formMetadata.MenuView.Dense = true;
        if (formMetadata.Views.filter(v => !Utilities.IsNullOrUndefinedOrEmpty(v.HiddenProperty) || !Utilities.IsNullOrUndefinedOrEmpty(v.DisabledProperty)).length === 0)
        {
            this._hasViewsWithHiddenOrDisabledProp = false;
        }
        if (formMetadata.EnableViewShortcuts)
        {
            this.datacontroller["$formId"] = formMetadata.FormId;
            this.$viewShortcutService.LoadShortcuts(formMetadata);
        }
        this.isInitialized = true;
    }

    private _notHiddenViews: IViewMetadata[];
    private _notDisabledViews: IViewMetadata[];
    private _hasViewsWithHiddenOrDisabledProp: boolean = true;

    ngDoCheck(): void
    {
        if (this._hasViewsWithHiddenOrDisabledProp)
        {
            if (!Utilities.arraysEqual(this.formMetadata.Views.filter(v => !this.isHidden(v)), this._notHiddenViews))
            {
                this._notHiddenViews = this.formMetadata.Views.filter(v => !this.isHidden(v));
                this.updateNavTree(this._notHiddenViews);
            }
            if (!Utilities.arraysEqual(this.formMetadata.Views.filter(v => !this.isDisabled(v)), this._notDisabledViews))
            {
                this._notDisabledViews = this.formMetadata.Views.filter(v => !this.isDisabled(v));
                this.hiddenShortcuts = this.formMetadata.Views.filter(v => this.isDisabled(v)).map(x => `${x.Path ? `${x.Path}/` : ""}${x.Title}`);
            }
        }
    }


    get menuView(): IViewMetadata
    {
        return this.formMetadata.MenuView;
    }

    get nodes(): TreeNode[]
    {
        return this._navTree.nodes;
    }

    get hasMenuView(): boolean
    {
        return this.isInitialized && this.menuView && !this.isHidden(this.menuView);
    }

    scrollIntoView(): void
    {
        setTimeout(() =>
        {
            const view = document.querySelector(".selected-list-item");
            if (view) view.scrollIntoView(false);
        }, 5);
    }

    select(id: string): void
    {
        this.selecting = true;
        const selected = this._navTree.getLeafById(id);
        this._navTree.select(selected);
        this.onSelect.emit(selected.view);
        this.scrollIntoView();
    }

    context(event: OnContextEvent): void
    {
        const selected = this._navTree.getLeafById(event.Id);
        if (selected.datacontroller["$formId"])
            this.$viewShortcutService.OnContextMenu(event.Event, selected.view, selected.datacontroller["$formId"]);
    }

    private updateNavTree(visibleViews: IViewMetadata[]): void
    {
        this.treeRebuild$.next();

        const self = this;
        let viewFoundInNavTree: Leaf;
        this._navTree = visibleViews.reduce(reduceToTree, new Tree("$root", [], true));
        if (this.selectedView)
        {
            viewFoundInNavTree = findSelectedViewIfExists(this._navTree, this.selectedView);
        }
        const selectedView =
            viewFoundInNavTree === undefined
                ? (ArrayHelpers.first(this._navTree.nodes, x => Leaf.isLeaf(x)) as Leaf)
                : viewFoundInNavTree;
        this.select(selectedView.id);
        function reduceToTree(tree: Tree, view: IViewMetadata): Tree
        {
            if (!view.Path || view.Path.length === 0)
            {
                tree.nodes.push(new Leaf(view, self.model, self.datacontroller, self.$metadataService, self.$metadataViewService, self.$metadataErrorService, self.treeRebuild$));
            }
            else
            {
                const pathParts = view.Path.split(VIEW_PATH_SEPARATOR);
                const parent: Tree = pathParts.reduce(walkTree, tree);
                if (!parent)
                {
                    throw new Error(
                        "MetadataViewNavigationListController.updateNavTree |  walkTree could not find parent node????"
                    );
                }
                parent.nodes.push(new Leaf(view, self.model, self.datacontroller, self.$metadataService, self.$metadataViewService, self.$metadataErrorService, self.treeRebuild$));
            }
            return tree;
        }
        function findSelectedViewIfExists(current: TreeNode, viewMetadata: IViewMetadata): Leaf
        {
            if (Leaf.isLeaf(current))
            {
                const leaf = current;
                if (leaf.view === viewMetadata) return current;
                return undefined;
            }
            else
            {
                const tree = current;
                for (let i = 0; i < tree.nodes.length; i++)
                {
                    const possibleLeaf = findSelectedViewIfExists(tree.nodes[i], viewMetadata);
                    if (possibleLeaf !== undefined)
                    {
                        tree.expanded = true; //keep the current submenu expanded when navigating back to the main page
                        return possibleLeaf as Leaf;
                    }
                }
                return undefined;
            }
        }
        function walkTree(current: Tree, pathPart: string): Tree
        {
            const nonLeafNodes = current.nodes.filter((x: TreeNode) => typeof x !== "string") as Array<Tree>;
            let next = nonLeafNodes.find((x: Tree) => x.name === pathPart);
            if (!next)
            {
                next = new Tree(pathPart);
                current.nodes.push(next);
            }
            return next;
        }
    }

    goToView(view: ISelectOption): void
    {
        if (this.onSelect)
        {
            this.onSelect.emit(this.formMetadata.Views.find(x => `${x.Path ? `${x.Path}/` : ""}${x.Title}` === view.value));
        }
        requestAnimationFrame(() => this.shortcut.value = undefined);
    }

    public isHidden(view: IViewMetadata): boolean
    {
        return this.$metadataViewService.IsHidden(view, this.model, this.datacontroller);
    }

    private isDisabled(view: IViewMetadata): boolean
    {
        return this.$metadataViewService.IsDisabled(view, this.model, this.datacontroller);
    }
}
